import React, { useEffect, useMemo, useState } from "react";
import api from "../api/api";
import {
  FaBullhorn,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaAward,
  FaBookOpen,
  FaShieldAlt,
  FaExternalLinkAlt,
  FaRegCalendarAlt,
  FaUniversity,
  FaStar,
  FaUsers,
  FaQuoteLeft,
  FaPlay,
  FaTimes,
  FaSpinner,
  FaExclamationCircle,
  FaImage,
} from "react-icons/fa";

const HomeHeader = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [slideError, setSlideError] = useState("");
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchSlides = async () => {
      try {
        setLoading(true);
        setSlideError("");

        const response = await api.get("/api/slides/");
        const payload = response?.data;
        const slideData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.results)
          ? payload.results
          : [];

        if (mounted) {
          setSlides(slideData);
          setCurrentSlide(0);
        }
      } catch (error) {
        if (mounted) {
          setSlideError(
            "We could not load the school slideshow. Please try again later."
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchSlides();

    return () => {
      mounted = false;
    };
  }, []);

  const normalizedSlides = useMemo(() => {
    return slides
      .filter(Boolean)
      .map((slide, index) => ({
        ...slide,
        _key: slide.id ?? slide.pk ?? index,
        _title:
          slide.title ||
          slide.heading ||
          slide.name ||
          "Welcome to Zion Praise Academy",
        _description:
          slide.description ||
          slide.subtitle ||
          slide.caption ||
          "Building knowledge, character, discipline, and a brighter future.",
        _image:
          slide.image_url ||
          slide.image ||
          slide.photo ||
          slide.banner ||
          "",
        _link: slide.link || slide.url || slide.button_url || "",
        _buttonText:
          slide.button_text || slide.button_label || "Discover Our School",
      }));
  }, [slides]);

  const activeSlide = normalizedSlides[currentSlide] || null;

  useEffect(() => {
    if (paused || normalizedSlides.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setCurrentSlide((previous) => (previous + 1) % normalizedSlides.length);
    }, 6500);

    return () => window.clearInterval(timer);
  }, [paused, normalizedSlides.length]);

  const goToSlide = (index) => {
    if (!normalizedSlides.length) return;

    setCurrentSlide(
      (index + normalizedSlides.length) % normalizedSlides.length
    );
  };

  const handleSlideAction = () => {
    if (activeSlide?._link) {
      window.location.href = activeSlide._link;
    } else {
      document
        .getElementById("about-zion")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const announcements = [
    {
      title: "Academic Excellence",
      description:
        "A learning environment that encourages academic growth, curiosity, and achievement.",
      icon: <FaBookOpen />,
    },
    {
      title: "Character & Discipline",
      description:
        "We value respect, responsibility, integrity, and positive student development.",
      icon: <FaShieldAlt />,
    },
    {
      title: "A Community That Cares",
      description:
        "We believe students thrive when school, families, and the wider community work together.",
      icon: <FaUsers />,
    },
  ];

  const highlights = [
    {
      icon: <FaGraduationCap />,
      title: "Student Development",
      text: "Supporting learners as they build knowledge, confidence, and responsibility.",
    },
    {
      icon: <FaChalkboardTeacher />,
      title: "Teaching & Learning",
      text: "Promoting meaningful classroom learning and a culture of continuous improvement.",
    },
    {
      icon: <FaAward />,
      title: "Achievement & Growth",
      text: "Encouraging every student to work toward personal and academic goals.",
    },
  ];

  return (
    <div className="zpa-home">
      <style>{zpaStyles}</style>

      {/* Slim school identity strip — intentionally no navigation menu */}
      <div className="zpa-identity-strip">
        <div className="zpa-container zpa-identity-inner">
          <div className="zpa-identity-left">
            <span className="zpa-identity-mark">
              <FaUniversity />
            </span>
            <span>
              <strong>ZION PRAISE ACADEMY</strong>
              <small>SENIOR HIGH SCHOOL</small>
            </span>
          </div>

          <div className="zpa-identity-location">
            <span className="zpa-location-dot" />
            Monrovia, Liberia
          </div>
        </div>
      </div>

      {/* Main hero / slideshow */}
      <section
        className="zpa-hero"
        aria-label="Zion Praise Academy introduction"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="zpa-hero-background">
          {activeSlide?._image ? (
            <img
              key={activeSlide._key}
              className="zpa-hero-image"
              src={activeSlide._image}
              alt={activeSlide._title}
              onError={(event) => {
                event.currentTarget.style.visibility = "hidden";
              }}
            />
          ) : (
            <div className="zpa-hero-placeholder">
              <FaGraduationCap />
              <span>Zion Praise Academy</span>
            </div>
          )}
          <div className="zpa-hero-overlay" />
        </div>

        <div className="zpa-container zpa-hero-content">
          <div className="zpa-hero-copy">
            <div className="zpa-eyebrow zpa-eyebrow-light">
              <span className="zpa-eyebrow-line" />
              WELCOME TO ZION PRAISE ACADEMY
            </div>

            <h1>
              Preparing Students
              <br />
              for a <span>Brighter Future.</span>
            </h1>

            <p className="zpa-hero-intro">
              A senior high school community committed to learning, good
              character, discipline, and the development of young people
              prepared for tomorrow.
            </p>

            {activeSlide && (
              <div className="zpa-slide-caption" aria-live="polite">
                <span className="zpa-caption-kicker">SCHOOL SPOTLIGHT</span>
                <h2>{activeSlide._title}</h2>
                <p>{activeSlide._description}</p>
              </div>
            )}

            <div className="zpa-hero-actions">
              <button
                type="button"
                className="zpa-button zpa-button-gold"
                onClick={handleSlideAction}
              >
                {activeSlide?._link
                  ? activeSlide._buttonText
                  : "Discover Our School"}
                <FaArrowRight />
              </button>

              <button
                type="button"
                className="zpa-button zpa-button-outline"
                onClick={() =>
                  document
                    .getElementById("zpa-highlights")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                <span className="zpa-button-play">
                  <FaPlay />
                </span>
                Explore Our School
              </button>
            </div>

            <div className="zpa-hero-values">
              <span>
                <FaCheckCircle /> Learning
              </span>
              <span>
                <FaCheckCircle /> Character
              </span>
              <span>
                <FaCheckCircle /> Achievement
              </span>
            </div>
          </div>

          <div className="zpa-hero-side">
            <div className="zpa-hero-side-card">
              <span className="zpa-side-card-icon">
                <FaGraduationCap />
              </span>
              <span className="zpa-side-card-label">OUR COMMITMENT</span>
              <strong>Knowledge. Character. Purpose.</strong>
              <span className="zpa-side-card-line" />
              <p>
                Helping students grow through education, positive values, and
                purposeful preparation for the future.
              </p>
            </div>

            <div className="zpa-side-note">
              <span className="zpa-side-note-star">
                <FaStar />
              </span>
              <span>
                <strong>Every learner matters.</strong>
                <small>Every opportunity counts.</small>
              </span>
            </div>
          </div>
        </div>

        {/* Slide navigation */}
        {normalizedSlides.length > 1 && (
          <div className="zpa-container zpa-slider-footer">
            <div className="zpa-slider-status">
              <span className="zpa-slider-current">
                {String(currentSlide + 1).padStart(2, "0")}
              </span>
              <span className="zpa-slider-divider" />
              <span className="zpa-slider-total">
                {String(normalizedSlides.length).padStart(2, "0")}
              </span>
              <span className="zpa-slider-label">SCHOOL GALLERY</span>
            </div>

            <div className="zpa-slider-controls">
              <div className="zpa-slider-dots">
                {normalizedSlides.map((slide, index) => (
                  <button
                    key={slide._key}
                    type="button"
                    className={`zpa-slider-dot ${
                      currentSlide === index ? "active" : ""
                    }`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Show slide ${index + 1}`}
                    aria-pressed={currentSlide === index}
                  />
                ))}
              </div>

              <button
                type="button"
                className="zpa-slider-arrow"
                onClick={() => goToSlide(currentSlide - 1)}
                aria-label="Previous slide"
              >
                <FaChevronLeft />
              </button>

              <button
                type="button"
                className="zpa-slider-arrow"
                onClick={() => goToSlide(currentSlide + 1)}
                aria-label="Next slide"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="zpa-loading-indicator">
            <FaSpinner className="zpa-spin" />
            <span>Loading school gallery...</span>
          </div>
        )}
      </section>

      {/* Important notices */}
      <section className="zpa-announcement-bar">
        <div className="zpa-container zpa-announcement-inner">
          <div className="zpa-announcement-label">
            <span className="zpa-announcement-icon">
              <FaBullhorn />
            </span>
            <span>
              <strong>SCHOOL UPDATES</strong>
              <small>Stay informed</small>
            </span>
          </div>

          <div className="zpa-announcement-message">
            <span className="zpa-announcement-live" />
            Welcome to Zion Praise Academy Senior High School.
            <span className="zpa-announcement-sub">
              Follow school announcements for important updates.
            </span>
          </div>

          <button
            type="button"
            className="zpa-announcement-button"
            onClick={() => setAnnouncementOpen((value) => !value)}
            aria-expanded={announcementOpen}
          >
            {announcementOpen ? "Close" : "Read More"}
            {announcementOpen ? <FaTimes /> : <FaArrowRight />}
          </button>
        </div>

        {announcementOpen && (
          <div className="zpa-container zpa-announcement-expanded">
            <div className="zpa-announcement-expanded-icon">
              <FaRegCalendarAlt />
            </div>
            <div>
              <strong>School Announcements</strong>
              <p>
                Please stay in contact with the school for confirmed
                information about academic activities, school events,
                enrollment, and important dates. This section can be connected
                to your official announcement data when available.
              </p>
            </div>
          </div>
        )}
      </section>

      {slideError && (
        <div className="zpa-container">
          <div className="zpa-slide-message zpa-slide-error" role="status">
            <FaExclamationCircle />
            <span>{slideError}</span>
          </div>
        </div>
      )}

      {!loading && !slideError && normalizedSlides.length === 0 && (
        <div className="zpa-container">
          <div className="zpa-slide-message">
            <FaImage />
            <span>
              The school gallery is currently empty. The welcome section
              remains available while slides are added.
            </span>
          </div>
        </div>
      )}

      {/* School introduction */}
      <section className="zpa-about-section" id="about-zion">
        <div className="zpa-container">
          <div className="zpa-about-grid">
            <div className="zpa-about-visual">
              <div className="zpa-about-image-frame">
                {activeSlide?._image ? (
                  <img
                    src={activeSlide._image}
                    alt="Zion Praise Academy school community"
                    loading="lazy"
                  />
                ) : (
                  <div className="zpa-about-placeholder">
                    <FaUniversity />
                    <span>Zion Praise Academy</span>
                  </div>
                )}
              </div>

              <div className="zpa-about-gold-block" />

              <div className="zpa-about-stamp">
                <span className="zpa-stamp-icon">
                  <FaGraduationCap />
                </span>
                <strong>LEARNING</strong>
                <span>WITH PURPOSE</span>
              </div>

              <div className="zpa-about-vertical-label">
                ZION PRAISE ACADEMY · MONROVIA
              </div>
            </div>

            <div className="zpa-about-copy">
              <div className="zpa-eyebrow">
                <span className="zpa-eyebrow-line" />
                ABOUT OUR SCHOOL
              </div>

              <h2>
                Education that builds
                <br />
                <span>more than knowledge.</span>
              </h2>

              <p className="zpa-about-lead">
                At Zion Praise Academy Senior High School, education is about
                developing capable learners and responsible young people.
              </p>

              <p className="zpa-about-description">
                We believe that a strong school experience combines academic
                learning with discipline, respect, personal responsibility,
                and preparation for future opportunities. Our goal is to
                encourage students to discover their abilities and pursue
                their potential.
              </p>

              <div className="zpa-about-principles">
                <div className="zpa-principle">
                  <span className="zpa-principle-icon">
                    <FaBookOpen />
                  </span>
                  <div>
                    <strong>Meaningful Learning</strong>
                    <p>Encouraging curiosity and academic progress.</p>
                  </div>
                </div>

                <div className="zpa-principle">
                  <span className="zpa-principle-icon">
                    <FaShieldAlt />
                  </span>
                  <div>
                    <strong>Strong Character</strong>
                    <p>Promoting respect, discipline, and integrity.</p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="zpa-text-link"
                onClick={() =>
                  document
                    .getElementById("zpa-highlights")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Discover what guides us <FaArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* School priorities */}
      <section className="zpa-highlights-section" id="zpa-highlights">
        <div className="zpa-container">
          <div className="zpa-section-heading">
            <div>
              <div className="zpa-eyebrow">
                <span className="zpa-eyebrow-line" />
                THE ZION EXPERIENCE
              </div>
              <h2>
                A foundation for <span>future success.</span>
              </h2>
              <p>
                Supporting the whole student through learning, values, and
                opportunities for growth.
              </p>
            </div>

            <div className="zpa-heading-mark">
              <FaGraduationCap />
            </div>
          </div>

          <div className="zpa-highlight-grid">
            {highlights.map((item, index) => (
              <article className="zpa-highlight-card" key={item.title}>
                <div className="zpa-card-topline">
                  <span className="zpa-card-number">
                    0{index + 1}
                  </span>
                  <span className="zpa-highlight-icon">{item.icon}</span>
                </div>

                <h3>{item.title}</h3>
                <p>{item.text}</p>

                <div className="zpa-card-bottom">
                  <span>OUR PRIORITIES</span>
                  <span className="zpa-card-arrow">
                    <FaArrowRight />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* School values */}
      <section className="zpa-values-section">
        <div className="zpa-values-pattern" />

        <div className="zpa-container zpa-values-content">
          <div className="zpa-values-heading">
            <div className="zpa-eyebrow zpa-eyebrow-light">
              <span className="zpa-eyebrow-line" />
              WHAT WE STAND FOR
            </div>
            <h2>
              Our values shape
              <br />
              <span>our future.</span>
            </h2>
            <p>
              The best learning environments help students develop both the
              knowledge they need and the character to use it well.
            </p>
          </div>

          <div className="zpa-values-list">
            {announcements.map((item, index) => (
              <div className="zpa-value-item" key={item.title}>
                <span className="zpa-value-index">
                  0{index + 1}
                </span>
                <span className="zpa-value-icon">{item.icon}</span>
                <div className="zpa-value-copy">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
                <FaArrowRight className="zpa-value-arrow" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing call to action */}
      <section className="zpa-cta-section">
        <div className="zpa-container">
          <div className="zpa-cta-panel">
            <div className="zpa-cta-emblem">
              <FaGraduationCap />
            </div>

            <div className="zpa-cta-copy">
              <span>WELCOME TO OUR SCHOOL COMMUNITY</span>
              <h2>
                The future begins with
                <br />
                <strong>a strong foundation.</strong>
              </h2>
              <p>
                Learn more about Zion Praise Academy Senior High School in
                Monrovia, Liberia.
              </p>
            </div>

            <button
              type="button"
              className="zpa-button zpa-button-dark"
              onClick={() =>
                document
                  .getElementById("about-zion")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Explore Zion <FaArrowRight />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="zpa-footer">
        <div className="zpa-container zpa-footer-main">
          <div className="zpa-footer-brand">
            <span className="zpa-footer-emblem">
              <FaUniversity />
            </span>
            <div>
              <strong>ZION PRAISE ACADEMY</strong>
              <span>SENIOR HIGH SCHOOL</span>
              <p>Monrovia, Liberia</p>
            </div>
          </div>

          <div className="zpa-footer-message">
            <span>EDUCATION · CHARACTER · PURPOSE</span>
            <p>Preparing learners for tomorrow.</p>
          </div>

          <button
            type="button"
            className="zpa-footer-top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Back to top <FaArrowRight />
          </button>
        </div>

        <div className="zpa-footer-bottom">
          <div className="zpa-container zpa-footer-bottom-inner">
            <span>
              © {new Date().getFullYear()} Zion Praise Academy Senior High
              School. All rights reserved.
            </span>
            <span className="zpa-footer-accent">
              <FaStar /> Learning with purpose
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const zpaStyles = `
  .zpa-home {
    --zpa-black: #151515;
    --zpa-black-soft: #202020;
    --zpa-gold: #D5A52B;
    --zpa-gold-light: #F0D17B;
    --zpa-white: #FFFFFF;
    --zpa-paper: #F7F7F5;
    --zpa-gray: #6B6B6B;
    --zpa-border: #E8E5DD;
    width: 100%;
    overflow: hidden;
    background: var(--zpa-white);
    color: var(--zpa-black);
    font-family: Inter, "Segoe UI", Arial, sans-serif;
    line-height: 1.6;
  }

  .zpa-home *,
  .zpa-home *::before,
  .zpa-home *::after {
    box-sizing: border-box;
  }

  .zpa-home button {
    font: inherit;
  }

  .zpa-container {
    width: min(1220px, calc(100% - 64px));
    margin: 0 auto;
  }

  .zpa-identity-strip {
    position: relative;
    z-index: 5;
    background: #FFFFFF;
    border-bottom: 1px solid #ECE8DF;
  }

  .zpa-identity-inner {
    min-height: 88px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }

  .zpa-identity-left {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  .zpa-identity-mark {
    width: 49px;
    height: 49px;
    flex: 0 0 49px;
    display: grid;
    place-items: center;
    color: #171717;
    background: #F6E7B6;
    border: 1px solid #E5C86C;
    border-radius: 5px;
    font-size: 23px;
  }

  .zpa-identity-left strong,
  .zpa-identity-left small {
    display: block;
  }

  .zpa-identity-left strong {
    font-size: 15px;
    letter-spacing: 1.3px;
    font-weight: 850;
    line-height: 1.5;
  }

  .zpa-identity-left small {
    margin-top: 2px;
    color: #787878;
    font-size: 10px;
    font-weight: 750;
    letter-spacing: 2.4px;
  }

  .zpa-identity-location {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #555555;
    font-size: 12px;
    font-weight: 650;
  }

  .zpa-location-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--zpa-gold);
    box-shadow: 0 0 0 4px #F8F0D9;
  }

  .zpa-hero {
    min-height: 630px;
    position: relative;
    isolation: isolate;
    background: #171717;
    color: #FFFFFF;
  }

  .zpa-hero-background,
  .zpa-hero-overlay {
    position: absolute;
    inset: 0;
  }

  .zpa-hero-background {
    z-index: -2;
    overflow: hidden;
    background:
      radial-gradient(ellipse at 75% 40%, #3A3426 0%, transparent 43%),
      linear-gradient(115deg, #111111 0%, #25221B 100%);
  }

  .zpa-hero-image {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    object-fit: cover;
    object-position: center;
    animation: zpa-image-appear 700ms ease both;
  }

  .zpa-hero-overlay {
    z-index: 1;
    background:
      linear-gradient(90deg, rgba(14,14,14,.96) 0%, rgba(17,17,17,.91) 35%, rgba(18,18,18,.70) 65%, rgba(15,15,15,.52) 100%),
      linear-gradient(0deg, rgba(9,9,9,.75) 0%, transparent 45%, rgba(0,0,0,.08) 100%);
  }

  .zpa-hero-placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    color: rgba(240,209,123,.35);
    font-size: 16px;
    letter-spacing: 2px;
  }

  .zpa-hero-placeholder svg {
    font-size: 100px;
  }

  .zpa-hero-content {
    min-height: 570px;
    padding-top: 78px;
    padding-bottom: 72px;
    display: grid;
    grid-template-columns: minmax(0, 1.55fr) minmax(240px, .65fr);
    align-items: center;
    gap: 60px;
  }

  .zpa-hero-copy {
    max-width: 760px;
    animation: zpa-rise-in 650ms ease both;
  }

  .zpa-eyebrow {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 19px;
    color: #7A622A;
    font-size: 10px;
    font-weight: 850;
    letter-spacing: 2.5px;
    line-height: 1.5;
  }

  .zpa-eyebrow-light {
    color: var(--zpa-gold-light);
  }

  .zpa-eyebrow-line {
    width: 28px;
    height: 2px;
    flex: 0 0 28px;
    background: var(--zpa-gold);
  }

  .zpa-hero-copy h1 {
    max-width: 760px;
    margin: 0;
    color: #FFFFFF;
    font-size: clamp(40px, 5.1vw, 68px);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -2.4px;
  }

  .zpa-hero-copy h1 span {
    color: var(--zpa-gold-light);
  }

  .zpa-hero-intro {
    max-width: 560px;
    margin: 24px 0 0;
    color: #E0DED9;
    font-size: 15px;
    line-height: 1.9;
  }

  .zpa-slide-caption {
    max-width: 570px;
    margin-top: 23px;
    padding-left: 15px;
    border-left: 2px solid var(--zpa-gold);
    animation: zpa-rise-in 350ms ease both;
  }

  .zpa-caption-kicker {
    color: var(--zpa-gold-light);
    font-size: 9px;
    font-weight: 850;
    letter-spacing: 2px;
  }

  .zpa-slide-caption h2 {
    margin: 4px 0 3px;
    color: #FFFFFF;
    font-size: 18px;
    line-height: 1.5;
    font-weight: 750;
  }

  .zpa-slide-caption p {
    margin: 0;
    color: #D0CECA;
    font-size: 12px;
    line-height: 1.8;
  }

  .zpa-hero-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 13px;
    margin-top: 30px;
  }

  .zpa-button {
    min-height: 49px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 13px;
    padding: 0 21px;
    border: 1px solid transparent;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px !important;
    font-weight: 800 !important;
    transition: background .2s ease, color .2s ease, border-color .2s ease, transform .2s ease;
  }

  .zpa-button:hover {
    transform: translateY(-2px);
  }

  .zpa-button svg {
    font-size: 12px;
  }

  .zpa-button-gold {
    background: var(--zpa-gold);
    color: #171717;
    border-color: var(--zpa-gold);
  }

  .zpa-button-gold:hover {
    background: #E9C35F;
    border-color: #E9C35F;
  }

  .zpa-button-outline {
    background: rgba(255,255,255,.035);
    color: #FFFFFF;
    border-color: rgba(255,255,255,.35);
  }

  .zpa-button-outline:hover {
    background: rgba(255,255,255,.12);
    border-color: #FFFFFF;
  }

  .zpa-button-play {
    width: 25px;
    height: 25px;
    display: grid;
    place-items: center;
    color: #191919;
    background: var(--zpa-gold-light);
    border-radius: 50%;
    font-size: 9px;
  }

  .zpa-hero-values {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 19px;
    margin-top: 28px;
    color: #D7D5CF;
    font-size: 11px;
    font-weight: 600;
  }

  .zpa-hero-values span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .zpa-hero-values svg {
    color: var(--zpa-gold-light);
    font-size: 12px;
  }

  .zpa-hero-side {
    width: 100%;
    max-width: 300px;
    justify-self: end;
  }

  .zpa-hero-side-card {
    position: relative;
    padding: 28px 26px 27px;
    background: rgba(255,255,255,.96);
    color: #181818;
    border-top: 4px solid var(--zpa-gold);
    box-shadow: 0 18px 50px rgba(0,0,0,.2);
  }

  .zpa-side-card-icon {
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    margin-bottom: 22px;
    background: #F5EAC9;
    color: #252016;
    font-size: 22px;
  }

  .zpa-side-card-label {
    display: block;
    margin-bottom: 8px;
    color: #86691E;
    font-size: 9px;
    font-weight: 850;
    letter-spacing: 2px;
  }

  .zpa-hero-side-card > strong {
    display: block;
    font-size: 22px;
    font-weight: 850;
    line-height: 1.35;
    letter-spacing: -.5px;
  }

  .zpa-side-card-line {
    display: block;
    width: 40px;
    height: 2px;
    margin: 19px 0;
    background: var(--zpa-gold);
  }

  .zpa-hero-side-card p {
    margin: 0;
    color: #686868;
    font-size: 12px;
    line-height: 1.9;
  }

  .zpa-side-note {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
    padding: 15px 17px;
    border: 1px solid rgba(255,255,255,.2);
    background: rgba(15,15,15,.68);
  }

  .zpa-side-note-star {
    width: 33px;
    height: 33px;
    flex: 0 0 33px;
    display: grid;
    place-items: center;
    color: #171717;
    background: var(--zpa-gold-light);
    border-radius: 50%;
  }

  .zpa-side-note strong,
  .zpa-side-note small {
    display: block;
  }

  .zpa-side-note strong {
    color: #FFFFFF;
    font-size: 11px;
  }

  .zpa-side-note small {
    margin-top: 2px;
    color: #D3D0C7;
    font-size: 10px;
  }

  .zpa-slider-footer {
    min-height: 60px;
    position: absolute;
    z-index: 3;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    border-top: 1px solid rgba(255,255,255,.17);
  }

  .zpa-slider-status,
  .zpa-slider-controls,
  .zpa-slider-dots {
    display: flex;
    align-items: center;
  }

  .zpa-slider-status {
    gap: 10px;
  }

  .zpa-slider-current {
    color: var(--zpa-gold-light);
    font-size: 13px;
    font-weight: 850;
  }

  .zpa-slider-divider {
    width: 23px;
    height: 1px;
    background: rgba(255,255,255,.5);
  }

  .zpa-slider-total {
    color: #D0CECA;
    font-size: 11px;
  }

  .zpa-slider-label {
    margin-left: 13px;
    color: #D0CECA;
    font-size: 9px;
    font-weight: 750;
    letter-spacing: 1.7px;
  }

  .zpa-slider-controls {
    gap: 10px;
  }

  .zpa-slider-dots {
    gap: 6px;
    margin-right: 13px;
  }

  .zpa-slider-dot {
    width: 7px;
    height: 7px;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: rgba(255,255,255,.45);
    cursor: pointer;
    transition: width .2s ease, background .2s ease;
  }

  .zpa-slider-dot.active {
    width: 25px;
    background: var(--zpa-gold);
  }

  .zpa-slider-arrow {
    width: 33px;
    height: 33px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255,255,255,.3);
    border-radius: 3px;
    background: transparent;
    color: #FFFFFF;
    cursor: pointer;
    font-size: 11px;
    transition: background .2s ease, color .2s ease;
  }

  .zpa-slider-arrow:hover {
    background: var(--zpa-gold);
    border-color: var(--zpa-gold);
    color: #171717;
  }

  .zpa-loading-indicator {
    position: absolute;
    z-index: 4;
    top: 20px;
    right: 25px;
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 9px 12px;
    border: 1px solid rgba(255,255,255,.16);
    border-radius: 3px;
    background: rgba(0,0,0,.5);
    color: #F0EDE4;
    font-size: 10px;
  }

  .zpa-spin {
    animation: zpa-spin 1s linear infinite;
  }

  .zpa-announcement-bar {
    background: #F6F5F1;
    border-bottom: 1px solid #EAE7DE;
  }

  .zpa-announcement-inner {
    min-height: 83px;
    display: grid;
    grid-template-columns: 210px minmax(0, 1fr) auto;
    align-items: center;
    gap: 27px;
  }

  .zpa-announcement-label {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-right: 22px;
    border-right: 1px solid #DEDAD0;
  }

  .zpa-announcement-icon {
    width: 39px;
    height: 39px;
    flex: 0 0 39px;
    display: grid;
    place-items: center;
    color: #171717;
    background: var(--zpa-gold);
    border-radius: 3px;
    font-size: 16px;
  }

  .zpa-announcement-label strong,
  .zpa-announcement-label small {
    display: block;
  }

  .zpa-announcement-label strong {
    font-size: 10px;
    letter-spacing: .7px;
    font-weight: 850;
  }

  .zpa-announcement-label small {
    margin-top: 3px;
    color: #77736B;
    font-size: 10px;
  }

  .zpa-announcement-message {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    color: #292929;
    font-size: 12px;
    font-weight: 750;
  }

  .zpa-announcement-live {
    width: 7px;
    height: 7px;
    flex: 0 0 7px;
    border-radius: 50%;
    background: #B38A21;
  }

  .zpa-announcement-sub {
    color: #77736B;
    font-size: 11px;
    font-weight: 450;
  }

  .zpa-announcement-button {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 10px 0 10px 12px;
    border: 0;
    background: transparent;
    color: #262626;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
  }

  .zpa-announcement-button svg {
    color: #9B761B;
    font-size: 11px;
  }

  .zpa-announcement-expanded {
    display: flex;
    align-items: flex-start;
    gap: 17px;
    padding-top: 0;
    padding-bottom: 22px;
    animation: zpa-rise-in 250ms ease both;
  }

  .zpa-announcement-expanded-icon {
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    display: grid;
    place-items: center;
    color: #171717;
    background: #EED58A;
  }

  .zpa-announcement-expanded strong {
    font-size: 13px;
  }

  .zpa-announcement-expanded p {
    max-width: 800px;
    margin: 5px 0 0;
    color: #66625A;
    font-size: 12px;
    line-height: 1.8;
  }

  .zpa-slide-message {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 20px;
    padding: 15px 17px;
    border: 1px solid #E5E0D3;
    background: #FBFAF7;
    color: #5F5B52;
    font-size: 12px;
    line-height: 1.7;
  }

  .zpa-slide-message svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: #A58126;
  }

  .zpa-slide-error {
    border-color: #E9D6D3;
    background: #FFF9F8;
    color: #7A3F39;
  }

  .zpa-slide-error svg {
    color: #B04439;
  }

  .zpa-about-section {
    padding: 100px 0 105px;
    background: #FFFFFF;
    scroll-margin-top: 25px;
  }

  .zpa-about-grid {
    display: grid;
    grid-template-columns: minmax(0, .92fr) minmax(0, 1.08fr);
    align-items: center;
    gap: clamp(48px, 7vw, 95px);
  }

  .zpa-about-visual {
    min-height: 440px;
    position: relative;
    padding: 0 35px 35px 0;
  }

  .zpa-about-image-frame {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 405px;
    overflow: hidden;
    background: #E9E5DA;
    border: 1px solid #E7E2D5;
  }

  .zpa-about-image-frame img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: center;
  }

  .zpa-about-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
    background: linear-gradient(135deg, #F5F1E7, #E5DCC6);
    color: #9B7C31;
    font-size: 12px;
    font-weight: 750;
    letter-spacing: 1px;
  }

  .zpa-about-placeholder svg {
    font-size: 75px;
  }

  .zpa-about-gold-block {
    width: 55%;
    height: 180px;
    position: absolute;
    right: 0;
    bottom: 0;
    background: #D8B34C;
  }

  .zpa-about-stamp {
    width: 151px;
    height: 151px;
    position: absolute;
    z-index: 2;
    right: -9px;
    bottom: 61px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #171717;
    border: 5px solid #FFFFFF;
    color: #FFFFFF;
    text-align: center;
    box-shadow: 0 8px 25px rgba(0,0,0,.12);
  }

  .zpa-stamp-icon {
    margin-bottom: 6px;
    color: var(--zpa-gold-light);
    font-size: 27px;
  }

  .zpa-about-stamp strong {
    font-size: 12px;
    letter-spacing: 1.2px;
  }

  .zpa-about-stamp > span:last-child {
    margin-top: 3px;
    color: #E4D8B2;
    font-size: 8px;
    letter-spacing: 1.5px;
  }

  .zpa-about-vertical-label {
    position: absolute;
    left: -28px;
    bottom: 77px;
    color: #827D70;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 2px;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
  }

  .zpa-about-copy .zpa-eyebrow {
    margin-bottom: 17px;
  }

  .zpa-about-copy h2,
  .zpa-section-heading h2 {
    margin: 0;
    color: #171717;
    font-size: clamp(31px, 3.5vw, 44px);
    font-weight: 850;
    letter-spacing: -1.5px;
    line-height: 1.2;
  }

  .zpa-about-copy h2 span,
  .zpa-section-heading h2 span {
    color: #A57E20;
  }

  .zpa-about-lead {
    margin: 24px 0 13px;
    color: #373737;
    font-size: 15px;
    font-weight: 650;
    line-height: 1.85;
  }

  .zpa-about-description {
    margin: 0;
    color: #717171;
    font-size: 13px;
    line-height: 2;
  }

  .zpa-about-principles {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    margin-top: 27px;
    padding-top: 23px;
    border-top: 1px solid #EAE7DF;
  }

  .zpa-principle {
    display: flex;
    align-items: flex-start;
    gap: 11px;
  }

  .zpa-principle-icon {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    display: grid;
    place-items: center;
    background: #F6EBCB;
    color: #5F4B1C;
    font-size: 15px;
  }

  .zpa-principle strong {
    display: block;
    color: #242424;
    font-size: 11px;
    font-weight: 850;
  }

  .zpa-principle p {
    margin: 4px 0 0;
    color: #777777;
    font-size: 10px;
    line-height: 1.7;
  }

  .zpa-text-link {
    display: inline-flex;
    align-items: center;
    gap: 11px;
    margin-top: 27px;
    padding: 0 0 6px;
    border: 0;
    border-bottom: 1px solid #D8B34C;
    background: transparent;
    color: #242424;
    font-size: 12px;
    font-weight: 850;
    cursor: pointer;
  }

  .zpa-text-link svg {
    color: #A17B1F;
    transition: transform .2s ease;
  }

  .zpa-text-link:hover svg {
    transform: translateX(4px);
  }

  .zpa-highlights-section {
    padding: 83px 0 96px;
    background: #F7F6F2;
    scroll-margin-top: 25px;
    border-top: 1px solid #EFEBE2;
    border-bottom: 1px solid #EAE6DC;
  }

  .zpa-section-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 30px;
    margin-bottom: 43px;
  }

  .zpa-section-heading .zpa-eyebrow {
    margin-bottom: 15px;
  }

  .zpa-section-heading h2 {
    font-size: clamp(30px, 3.5vw, 43px);
  }

  .zpa-section-heading p {
    max-width: 570px;
    margin: 15px 0 0;
    color: #74716A;
    font-size: 13px;
    line-height: 1.9;
  }

  .zpa-heading-mark {
    width: 63px;
    height: 63px;
    flex: 0 0 63px;
    display: grid;
    place-items: center;
    margin-bottom: 6px;
    border: 1px solid #E4D6AD;
    background: #F1E5C5;
    color: #6F5518;
    font-size: 27px;
  }

  .zpa-highlight-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }

  .zpa-highlight-card {
    min-height: 255px;
    display: flex;
    flex-direction: column;
    padding: 26px 25px 22px;
    background: #FFFFFF;
    border: 1px solid #EAE6DD;
    border-top: 3px solid #D5AD42;
    box-shadow: 0 5px 18px rgba(28,26,20,.025);
    transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease;
  }

  .zpa-highlight-card:hover {
    transform: translateY(-5px);
    border-color: #D9C58E;
    box-shadow: 0 15px 32px rgba(28,26,20,.075);
  }

  .zpa-card-topline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 15px;
  }

  .zpa-card-number {
    color: #B7B0A0;
    font-size: 11px;
    font-weight: 850;
    letter-spacing: 1.5px;
  }

  .zpa-highlight-icon {
    width: 47px;
    height: 47px;
    display: grid;
    place-items: center;
    background: #F7EFD8;
    color: #6E551B;
    font-size: 22px;
  }

  .zpa-highlight-card h3 {
    margin: 24px 0 9px;
    color: #1E1E1E;
    font-size: 18px;
    font-weight: 850;
    line-height: 1.4;
    letter-spacing: -.3px;
  }

  .zpa-highlight-card > p {
    margin: 0 0 24px;
    color: #747474;
    font-size: 12px;
    line-height: 1.9;
  }

  .zpa-card-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: auto;
    padding-top: 17px;
    border-top: 1px solid #EFEBE3;
  }

  .zpa-card-bottom > span:first-child {
    color: #777065;
    font-size: 8px;
    font-weight: 850;
    letter-spacing: 1.5px;
  }

  .zpa-card-arrow {
    width: 27px;
    height: 27px;
    display: grid;
    place-items: center;
    background: #F5E9C7;
    color: #4D3B12;
    font-size: 10px;
  }

  .zpa-values-section {
    position: relative;
    overflow: hidden;
    padding: 94px 0;
    background: #181818;
    color: #FFFFFF;
  }

  .zpa-values-pattern {
    position: absolute;
    top: -160px;
    right: -130px;
    width: 570px;
    height: 570px;
    border: 1px solid rgba(213,165,43,.14);
    border-radius: 50%;
    pointer-events: none;
  }

  .zpa-values-pattern::before,
  .zpa-values-pattern::after {
    content: "";
    position: absolute;
    border: 1px solid rgba(213,165,43,.11);
    border-radius: 50%;
  }

  .zpa-values-pattern::before {
    inset: 45px;
  }

  .zpa-values-pattern::after {
    inset: 90px;
  }

  .zpa-values-content {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr);
    gap: 90px;
    align-items: start;
  }

  .zpa-values-heading .zpa-eyebrow {
    margin-bottom: 19px;
  }

  .zpa-values-heading h2 {
    margin: 0;
    color: #FFFFFF;
    font-size: clamp(36px, 4vw, 49px);
    font-weight: 850;
    letter-spacing: -1.7px;
    line-height: 1.19;
  }

  .zpa-values-heading h2 span {
    color: var(--zpa-gold-light);
  }

  .zpa-values-heading > p {
    max-width: 385px;
    margin: 21px 0 0;
    color: #C2C0BA;
    font-size: 13px;
    line-height: 2;
  }

  .zpa-values-list {
    border-top: 1px solid rgba(255,255,255,.17);
  }

  .zpa-value-item {
    display: grid;
    grid-template-columns: 26px 43px minmax(0, 1fr) 16px;
    align-items: start;
    gap: 15px;
    padding: 23px 0;
    border-bottom: 1px solid rgba(255,255,255,.17);
    transition: padding .2s ease;
  }

  .zpa-value-item:hover {
    padding-left: 6px;
  }

  .zpa-value-index {
    padding-top: 6px;
    color: var(--zpa-gold-light);
    font-size: 10px;
    font-weight: 850;
    letter-spacing: 1px;
  }

  .zpa-value-icon {
    width: 41px;
    height: 41px;
    display: grid;
    place-items: center;
    background: rgba(213,165,43,.14);
    color: var(--zpa-gold-light);
    font-size: 18px;
  }

  .zpa-value-copy h3 {
    margin: 0;
    color: #FFFFFF;
    font-size: 14px;
    font-weight: 800;
  }

  .zpa-value-copy p {
    margin: 6px 0 0;
    color: #C4C1BA;
    font-size: 11px;
    line-height: 1.8;
  }

  .zpa-value-arrow {
    margin-top: 7px;
    color: var(--zpa-gold-light);
    font-size: 11px;
  }

  .zpa-cta-section {
    padding: 75px 0;
    background: #F7F6F2;
  }

  .zpa-cta-panel {
    position: relative;
    display: grid;
    grid-template-columns: 75px minmax(0, 1fr) auto;
    align-items: center;
    gap: 27px;
    padding: 39px 43px;
    background: #EEDBA0;
    border: 1px solid #E4CE84;
  }

  .zpa-cta-emblem {
    width: 68px;
    height: 68px;
    display: grid;
    place-items: center;
    background: #181818;
    color: var(--zpa-gold-light);
    font-size: 30px;
  }

  .zpa-cta-copy > span {
    display: block;
    margin-bottom: 9px;
    color: #6E561D;
    font-size: 9px;
    font-weight: 850;
    letter-spacing: 1.8px;
  }

  .zpa-cta-copy h2 {
    margin: 0;
    color: #171717;
    font-size: clamp(22px, 2.6vw, 31px);
    font-weight: 750;
    line-height: 1.28;
    letter-spacing: -.8px;
  }

  .zpa-cta-copy h2 strong {
    font-weight: 900;
  }

  .zpa-cta-copy p {
    margin: 9px 0 0;
    color: #5F553A;
    font-size: 11px;
    line-height: 1.8;
  }

  .zpa-button-dark {
    min-width: 155px;
    background: #191919;
    color: #FFFFFF;
    border-color: #191919;
  }

  .zpa-button-dark:hover {
    background: #34302A;
    border-color: #34302A;
  }

  .zpa-footer {
    background: #151515;
    color: #FFFFFF;
  }

  .zpa-footer-main {
    min-height: 130px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
    padding-top: 27px;
    padding-bottom: 27px;
  }

  .zpa-footer-brand {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  .zpa-footer-emblem {
    width: 47px;
    height: 47px;
    flex: 0 0 47px;
    display: grid;
    place-items: center;
    background: #D7B44E;
    color: #171717;
    font-size: 21px;
  }

  .zpa-footer-brand strong,
  .zpa-footer-brand span,
  .zpa-footer-brand p {
    display: block;
  }

  .zpa-footer-brand strong {
    color: #FFFFFF;
    font-size: 12px;
    font-weight: 850;
    letter-spacing: 1px;
  }

  .zpa-footer-brand div > span {
    margin-top: 3px;
    color: var(--zpa-gold-light);
    font-size: 9px;
    font-weight: 750;
    letter-spacing: 1.5px;
  }

  .zpa-footer-brand p {
    margin: 5px 0 0;
    color: #BDBBB4;
    font-size: 10px;
  }

  .zpa-footer-message {
    text-align: center;
  }

  .zpa-footer-message span {
    color: var(--zpa-gold-light);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 1.7px;
  }

  .zpa-footer-message p {
    margin: 6px 0 0;
    color: #C6C3BC;
    font-size: 11px;
  }

  .zpa-footer-top {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 11px 0 11px 12px;
    border: 0;
    background: transparent;
    color: #FFFFFF;
    font-size: 11px;
    font-weight: 750;
    cursor: pointer;
  }

  .zpa-footer-top svg {
    color: var(--zpa-gold-light);
    transform: rotate(-45deg);
  }

  .zpa-footer-bottom {
    border-top: 1px solid rgba(255,255,255,.12);
  }

  .zpa-footer-bottom-inner {
    min-height: 53px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    color: #AAA79F;
    font-size: 10px;
  }

  .zpa-footer-accent {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #E5D7AE;
    white-space: nowrap;
  }

  .zpa-footer-accent svg {
    color: var(--zpa-gold-light);
    font-size: 10px;
  }

  @keyframes zpa-rise-in {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes zpa-image-appear {
    from { opacity: .65; }
    to { opacity: 1; }
  }

  @keyframes zpa-spin {
    to { transform: rotate(360deg); }
  }

  @media (min-width: 1500px) {
    .zpa-hero {
      min-height: 690px;
    }

    .zpa-hero-content {
      min-height: 625px;
    }

    .zpa-hero-copy h1 {
      font-size: 72px;
    }
  }

  @media (max-width: 1050px) {
    .zpa-hero-content {
      grid-template-columns: minmax(0, 1.3fr) minmax(215px, .7fr);
      gap: 32px;
    }

    .zpa-hero-copy h1 {
      font-size: clamp(38px, 5vw, 56px);
    }

    .zpa-hero-side-card {
      padding: 23px 21px;
    }

    .zpa-about-grid {
      gap: 48px;
    }

    .zpa-values-content {
      gap: 50px;
    }

    .zpa-announcement-inner {
      grid-template-columns: 190px minmax(0, 1fr) auto;
      gap: 18px;
    }

    .zpa-cta-panel {
      padding: 32px;
      gap: 20px;
    }
  }

  @media (max-width: 800px) {
    .zpa-container {
      width: min(100% - 38px, 650px);
    }

    .zpa-identity-inner {
      min-height: 75px;
    }

    .zpa-identity-mark {
      width: 43px;
      height: 43px;
      flex-basis: 43px;
    }

    .zpa-identity-left strong {
      font-size: 13px;
    }

    .zpa-identity-left small {
      font-size: 9px;
      letter-spacing: 1.8px;
    }

    .zpa-hero {
      min-height: auto;
    }

    .zpa-hero-content {
      min-height: 0;
      grid-template-columns: 1fr;
      gap: 32px;
      padding-top: 69px;
      padding-bottom: 105px;
    }

    .zpa-hero-copy {
      max-width: 620px;
    }

    .zpa-hero-copy h1 {
      font-size: clamp(39px, 7.5vw, 61px);
      letter-spacing: -1.8px;
    }

    .zpa-hero-intro {
      font-size: 14px;
    }

    .zpa-hero-side {
      display: none;
    }

    .zpa-hero-overlay {
      background:
        linear-gradient(90deg, rgba(14,14,14,.93), rgba(17,17,17,.76)),
        linear-gradient(0deg, rgba(9,9,9,.75), transparent 60%);
    }

    .zpa-slider-footer {
      min-height: 58px;
    }

    .zpa-announcement-inner {
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 12px 20px;
      padding-top: 17px;
      padding-bottom: 17px;
    }

    .zpa-announcement-label {
      grid-column: 1 / -1;
      padding: 0;
      border: 0;
    }

    .zpa-announcement-message {
      align-items: flex-start;
      font-size: 11px;
      line-height: 1.7;
    }

    .zpa-announcement-sub {
      flex-basis: 100%;
      margin-left: 17px;
    }

    .zpa-announcement-button {
      align-self: center;
    }

    .zpa-about-section {
      padding: 76px 0 80px;
    }

    .zpa-about-grid {
      grid-template-columns: 1fr;
      gap: 44px;
    }

    .zpa-about-visual {
      width: min(100%, 510px);
      min-height: 380px;
      margin: 0 auto;
    }

    .zpa-about-image-frame {
      height: 350px;
    }

    .zpa-about-stamp {
      right: -4px;
      bottom: 46px;
      width: 130px;
      height: 130px;
    }

    .zpa-about-gold-block {
      height: 150px;
    }

    .zpa-about-vertical-label {
      left: -18px;
    }

    .zpa-about-copy {
      max-width: 620px;
    }

    .zpa-highlights-section {
      padding: 70px 0 75px;
    }

    .zpa-highlight-grid {
      grid-template-columns: 1fr;
      gap: 15px;
    }

    .zpa-highlight-card {
      min-height: 0;
    }

    .zpa-highlight-card h3 {
      margin-top: 17px;
    }

    .zpa-values-section {
      padding: 75px 0;
    }

    .zpa-values-content {
      grid-template-columns: 1fr;
      gap: 35px;
    }

    .zpa-values-heading > p {
      max-width: 560px;
    }

    .zpa-cta-section {
      padding: 55px 0;
    }

    .zpa-cta-panel {
      grid-template-columns: 60px minmax(0, 1fr);
      padding: 28px;
      gap: 20px;
    }

    .zpa-cta-emblem {
      width: 58px;
      height: 58px;
      font-size: 25px;
    }

    .zpa-cta-panel .zpa-button {
      grid-column: 2;
      justify-self: start;
    }

    .zpa-footer-main {
      flex-wrap: wrap;
    }

    .zpa-footer-message {
      text-align: right;
    }
  }

  @media (max-width: 520px) {
    .zpa-container {
      width: calc(100% - 34px);
    }

    .zpa-identity-inner {
      min-height: 69px;
    }

    .zpa-identity-left {
      gap: 10px;
    }

    .zpa-identity-mark {
      width: 38px;
      height: 38px;
      flex-basis: 38px;
      font-size: 18px;
    }

    .zpa-identity-left strong {
      font-size: 11px;
      letter-spacing: .7px;
    }

    .zpa-identity-left small {
      font-size: 8px;
      letter-spacing: 1.3px;
    }

    .zpa-identity-location {
      gap: 6px;
      font-size: 10px;
    }

    .zpa-location-dot {
      width: 6px;
      height: 6px;
    }

    .zpa-hero-content {
      padding-top: 54px;
      padding-bottom: 94px;
    }

    .zpa-eyebrow {
      font-size: 9px;
      letter-spacing: 1.7px;
    }

    .zpa-hero-copy h1 {
      font-size: clamp(35px, 9.1vw, 45px);
      letter-spacing: -1.5px;
    }

    .zpa-hero-intro {
      margin-top: 18px;
      font-size: 12px;
      line-height: 1.9;
    }

    .zpa-slide-caption {
      margin-top: 18px;
    }

    .zpa-slide-caption h2 {
      font-size: 16px;
    }

    .zpa-slide-caption p {
      font-size: 11px;
    }

    .zpa-hero-actions {
      align-items: stretch;
      flex-direction: column;
      gap: 10px;
      margin-top: 24px;
    }

    .zpa-button {
      width: 100%;
      min-height: 47px;
    }

    .zpa-hero-values {
      gap: 10px 15px;
      font-size: 10px;
    }

    .zpa-slider-footer {
      min-height: 54px;
    }

    .zpa-slider-label {
      display: none;
    }

    .zpa-slider-dots {
      gap: 5px;
      margin-right: 5px;
    }

    .zpa-slider-arrow {
      width: 30px;
      height: 30px;
    }

    .zpa-loading-indicator {
      top: 10px;
      right: 10px;
      font-size: 9px;
    }

    .zpa-announcement-inner {
      grid-template-columns: minmax(0, 1fr);
      gap: 12px;
    }

    .zpa-announcement-label {
      grid-column: auto;
    }

    .zpa-announcement-message {
      padding: 4px 0;
    }

    .zpa-announcement-button {
      justify-self: start;
      padding-left: 0;
    }

    .zpa-announcement-expanded {
      padding-bottom: 18px;
    }

    .zpa-about-section {
      padding: 62px 0 65px;
    }

    .zpa-about-visual {
      min-height: 310px;
      padding: 0 22px 27px 0;
    }

    .zpa-about-image-frame {
      height: 285px;
    }

    .zpa-about-gold-block {
      height: 115px;
    }

    .zpa-about-stamp {
      width: 108px;
      height: 108px;
      right: -3px;
      bottom: 34px;
      border-width: 4px;
    }

    .zpa-stamp-icon {
      font-size: 21px;
    }

    .zpa-about-stamp strong {
      font-size: 10px;
    }

    .zpa-about-stamp > span:last-child {
      font-size: 7px;
    }

    .zpa-about-vertical-label {
      left: -12px;
      bottom: 54px;
      font-size: 7px;
    }

    .zpa-about-copy h2,
    .zpa-section-heading h2 {
      font-size: 31px;
      letter-spacing: -1px;
    }

    .zpa-about-lead {
      font-size: 13px;
    }

    .zpa-about-description {
      font-size: 12px;
    }

    .zpa-about-principles {
      grid-template-columns: 1fr;
      gap: 17px;
    }

    .zpa-highlights-section {
      padding: 61px 0 65px;
    }

    .zpa-section-heading {
      margin-bottom: 28px;
    }

    .zpa-heading-mark {
      width: 45px;
      height: 45px;
      flex-basis: 45px;
      font-size: 20px;
    }

    .zpa-section-heading p {
      font-size: 12px;
    }

    .zpa-highlight-card {
      padding: 22px 20px 19px;
    }

    .zpa-highlight-card h3 {
      font-size: 17px;
    }

    .zpa-values-section {
      padding: 63px 0;
    }

    .zpa-values-heading h2 {
      font-size: 37px;
    }

    .zpa-value-item {
      grid-template-columns: 19px 36px minmax(0, 1fr) 12px;
      gap: 10px;
      padding: 20px 0;
    }

    .zpa-value-icon {
      width: 36px;
      height: 36px;
      font-size: 15px;
    }

    .zpa-value-copy h3 {
      font-size: 12px;
    }

    .zpa-value-copy p {
      font-size: 10px;
    }

    .zpa-cta-section {
      padding: 35px 0;
    }

    .zpa-cta-panel {
      grid-template-columns: 1fr;
      gap: 17px;
      padding: 25px 21px;
    }

    .zpa-cta-emblem {
      width: 51px;
      height: 51px;
      font-size: 22px;
    }

    .zpa-cta-copy h2 {
      font-size: 25px;
    }

    .zpa-cta-panel .zpa-button {
      grid-column: 1;
      justify-self: stretch;
    }

    .zpa-footer-main {
      align-items: flex-start;
      flex-direction: column;
      gap: 23px;
      padding-top: 29px;
      padding-bottom: 25px;
    }

    .zpa-footer-message {
      text-align: left;
    }

    .zpa-footer-top {
      padding-left: 0;
    }

    .zpa-footer-bottom-inner {
      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
      gap: 8px;
      padding-top: 14px;
      padding-bottom: 14px;
      font-size: 9px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .zpa-home *,
    .zpa-home *::before,
    .zpa-home *::after {
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      scroll-behavior: auto !important;
      transition-duration: .01ms !important;
    }
  }
`;

export default HomeHeader;
